/**
 * TUVI - Módulo Posventa - Kanban de Garantías
 * Tablero Kanban con drag & drop, prioridades e informes avanzados
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { UPLOAD_URL } from '../../config';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './Posventa.css';

const ESTADOS = [
  { key: 'radicacion', label: 'Radicación de Solicitud' },
  { key: 'programacion_visita', label: 'Programación de Visita' },
  { key: 'ejecucion_garantias', label: 'Ejecución de Garantías' },
  { key: 'garantia_cerrada', label: 'Garantía Cerrada' },
];

const PRIORIDADES = [
  { key: 'alta', label: 'Alta', color: '#ef4444' },
  { key: 'media', label: 'Media', color: '#f59e0b' },
  { key: 'baja', label: 'Baja', color: '#22c55e' },
];

const PRIORIDAD_ORDEN = { alta: 0, media: 1, baja: 2 };

const TIPOS_SOLICITANTE = [
  { key: 'propietario', label: 'Propietario' },
  { key: 'arrendatario', label: 'Arrendatario' },
  { key: 'otro', label: 'Otro' },
];
const tipoSolicitanteLabel = (k) => TIPOS_SOLICITANTE.find(t => t.key === k)?.label || '';

const FORM_VACIO = { proyecto_nombre: '', apartamento: '', descripcion: '', proyecto_id: null, prioridad: 'media', solicitante_nombre: '', solicitante_telefono: '', solicitante_tipo: '', fecha_dano: '' };

// Solicitud que llegó por el agente de WhatsApp y que nadie ha abierto todavía.
const esNueva = (sol) => sol.origen === 'bot' && !sol.leida_en;

const urlAdjunto = (ruta) => `${UPLOAD_URL}/catalogo/img/evidencia-posventa/${String(ruta).split('/').map(encodeURIComponent).join('/')}`;
const formatBytes = (n) => {
  if (n == null) return '';
  const u = ['B', 'KB', 'MB', 'GB']; let i = 0; let v = Number(n);
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i >= 2 ? 1 : 0)} ${u[i]}`;
};

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const getPrioridadColor = (p) => PRIORIDADES.find(pr => pr.key === p)?.color || '#f59e0b';

const formatMoney = (valor) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(valor || 0);

export default function Posventa({ initialGastoProyecto } = {}) {
  const { success, error } = useNotification();
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ ...FORM_VACIO });
  const [busquedaProy, setBusquedaProy] = useState('');
  const [proyectos, setProyectos] = useState([]);
  const [showProyResults, setShowProyResults] = useState(false);
  const [vista, setVista] = useState('kanban');
  const [informe, setInforme] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [visitas, setVisitas] = useState([]);
  const [visitasSol, setVisitasSol] = useState(null);   // solicitud del modal de visitas
  const [visitasLoading, setVisitasLoading] = useState(false);
  const [dragItem, setDragItem] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [guardando, setGuardando] = useState(false);
  // Ficha de la solicitud (datos del solicitante y adjuntos; marca como leída al abrir)
  const [ficha, setFicha] = useState(null);
  const [soloSinLeer, setSoloSinLeer] = useState(false);
  const [filtroOrigen, setFiltroOrigen] = useState('todos');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  // Detalle de gasto por apartamento (facturas de posventa + comprobantes)
  const [gastoDetalle, setGastoDetalle] = useState(null);
  const [gastoLoading, setGastoLoading] = useState(false);
  // Consolidado (dashboard)
  const [consolidado, setConsolidado] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const d = await client.query(`{ posventaSolicitudes { id proyecto_id proyecto_nombre apartamento descripcion prioridad estado created_at updated_at solicitante_nombre solicitante_telefono solicitante_tipo fecha_dano origen whatsapp leida_en leida_por estado_residente adjuntos { id tipo nombre_archivo ruta_archivo mime tamano_bytes } historial { id estado_anterior estado_nuevo fecha_cambio } } }`);
      setSolicitudes(d.posventaSolicitudes || []);
    } catch (err) { error('Error cargando solicitudes'); }
    finally { setLoading(false); }
  }, [error]);

  useEffect(() => { cargar(); }, [cargar]);

  // Ver el gasto (facturas de posventa) de un apartamento/proyecto.
  // Funciona con proyecto vinculado O solo con apartamento (best-effort por
  // descripción), para que el link a proyecto sea secundario.
  const verGasto = useCallback(async (proy) => {
    const pid = proy?.proyecto_id || null;
    const apto = proy?.apartamento || null;
    if (!pid && !apto) { error('Esta solicitud no tiene proyecto ni apartamento'); return; }
    const base = { proyecto_id: pid, proyecto_nombre: proy.proyecto_nombre, apartamento: apto, vinculado: !!pid };
    setGastoLoading(true);
    setGastoDetalle({ ...base, facturas: [], total: 0 });
    try {
      const vars = {};
      const decl = [], args = [];
      if (pid) { decl.push('$pid: Int'); args.push('proyecto_id: $pid'); vars.pid = pid; }
      if (apto) { decl.push('$apto: String'); args.push('apartamento: $apto'); vars.apto = apto; }
      const d = await client.query(`query(${decl.join(', ')}) {
        posventaGasto(${args.join(', ')}) {
          total cantidad
          facturas { id tipo numero_factura fecha_emision monto saldo_pendiente estado descripcion proveedor_nombre archivo_factura archivos { nombre url } }
        }
      }`, vars);
      const g = d.posventaGasto || { total: 0, facturas: [] };
      setGastoDetalle({ ...base, facturas: g.facturas || [], total: g.total || 0 });
    } catch (err) { error('Error cargando el gasto'); setGastoDetalle(null); }
    finally { setGastoLoading(false); }
  }, [error]);

  // Acceso directo desde la tarjeta "Posventa" del Resumen del proyecto
  useEffect(() => {
    if (initialGastoProyecto?.proyecto_id) verGasto(initialGastoProyecto);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialGastoProyecto?.proyecto_id]);

  const buscarProyectos = async (texto) => {
    setBusquedaProy(texto);
    if (texto.length < 2) { setProyectos([]); setShowProyResults(false); return; }
    try {
      const d = await client.query(`{ proyectos { id proyecto_nombre apartamento nombre_cliente } }`);
      const filtrados = (d.proyectos || []).filter(p =>
        (p.proyecto_nombre || '').toLowerCase().includes(texto.toLowerCase()) ||
        (p.apartamento || '').toLowerCase().includes(texto.toLowerCase())
      ).slice(0, 10);
      setProyectos(filtrados);
      setShowProyResults(true);
    } catch {}
  };

  const seleccionarProyecto = (p) => {
    setForm({ ...form, proyecto_id: p.id, proyecto_nombre: p.proyecto_nombre, apartamento: p.apartamento || '' });
    setBusquedaProy(`${p.proyecto_nombre} - ${p.apartamento || ''}`);
    setShowProyResults(false);
  };

  const handleGuardar = async () => {
    if (!form.proyecto_nombre || !form.proyecto_nombre.trim()) { error('Ingresa el nombre del proyecto'); return; }
    setGuardando(true);
    try {
      if (editando) {
        const r = await client.mutate(`mutation($id: Int!, $input: SolicitudPosventaInput!) { editarSolicitudPosventa(id: $id, input: $input) { success message } }`,
          { id: editando.id, input: { proyecto_id: form.proyecto_id, proyecto_nombre: form.proyecto_nombre, apartamento: form.apartamento, descripcion: form.descripcion, prioridad: form.prioridad, solicitante_nombre: form.solicitante_nombre || null, solicitante_telefono: form.solicitante_telefono || null, solicitante_tipo: form.solicitante_tipo || null, fecha_dano: form.fecha_dano || null } });
        if (r.editarSolicitudPosventa.success) success('Solicitud actualizada');
      } else {
        const r = await client.mutate(`mutation($input: SolicitudPosventaInput!) { crearSolicitudPosventa(input: $input) { success message id } }`,
          { input: { proyecto_id: form.proyecto_id, proyecto_nombre: form.proyecto_nombre, apartamento: form.apartamento, descripcion: form.descripcion, prioridad: form.prioridad, solicitante_nombre: form.solicitante_nombre || null, solicitante_telefono: form.solicitante_telefono || null, solicitante_tipo: form.solicitante_tipo || null, fecha_dano: form.fecha_dano || null } });
        if (r.crearSolicitudPosventa.success) success('Solicitud creada');
      }
      setShowForm(false); setEditando(null);
      setForm({ ...FORM_VACIO });
      setBusquedaProy('');
      cargar();
    } catch (err) { error('Error guardando'); }
    finally { setGuardando(false); }
  };

  const handleEditar = (sol) => {
    setEditando(sol);
    setForm({ proyecto_id: sol.proyecto_id, proyecto_nombre: sol.proyecto_nombre, apartamento: sol.apartamento || '', descripcion: sol.descripcion || '', prioridad: sol.prioridad || 'media', solicitante_nombre: sol.solicitante_nombre || '', solicitante_telefono: sol.solicitante_telefono || '', solicitante_tipo: sol.solicitante_tipo || '', fecha_dano: sol.fecha_dano || '' });
    setBusquedaProy(sol.proyecto_nombre);
    setShowForm(true);
  };

  // Ficha completa: datos del solicitante, descripción y adjuntos. Si llegó por
  // WhatsApp y nadie la había abierto, se marca como leída (deja de ser "Nuevo").
  const verFicha = async (sol) => {
    setFicha(sol);
    if (!esNueva(sol)) return;
    try {
      const r = await client.mutate(`mutation($id: Int!) { marcarLeidaPosventa(id: $id) { success } }`, { id: sol.id });
      if (r.marcarLeidaPosventa?.success) {
        setSolicitudes(prev => prev.map(s => (s.id === sol.id ? { ...s, leida_en: new Date().toISOString() } : s)));
      }
    } catch {}
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('Eliminar esta solicitud?')) return;
    try {
      await client.mutate(`mutation($id: Int!) { eliminarSolicitudPosventa(id: $id) { success } }`, { id });
      success('Solicitud eliminada'); cargar();
    } catch { error('Error eliminando'); }
  };

  // Drag & Drop
  const handleDragStart = (e, sol) => { setDragItem(sol); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, estado) => { e.preventDefault(); setDragOver(estado); };
  const handleDragLeave = () => setDragOver(null);
  const handleDrop = async (e, nuevoEstado) => {
    e.preventDefault(); setDragOver(null);
    if (!dragItem || dragItem.estado === nuevoEstado) { setDragItem(null); return; }
    try {
      const r = await client.mutate(`mutation($id: Int!, $estado: String!) { cambiarEstadoPosventa(id: $id, estado: $estado) { success message } }`,
        { id: dragItem.id, estado: nuevoEstado });
      if (r.cambiarEstadoPosventa.success) { success(r.cambiarEstadoPosventa.message); cargar(); }
    } catch { error('Error cambiando estado'); }
    setDragItem(null);
  };

  // Informes
  const cargarInforme = async () => {
    try {
      const vars = {};
      const hasFiltro = fechaDesde || fechaHasta;
      let q;
      if (hasFiltro) {
        const p = []; const a = [];
        if (fechaDesde) { p.push('$fd: String'); a.push('fecha_desde: $fd'); vars.fd = fechaDesde; }
        if (fechaHasta) { p.push('$fh: String'); a.push('fecha_hasta: $fh'); vars.fh = fechaHasta; }
        q = `query(${p.join(', ')}) { posventaInformeGlobal(${a.join(', ')}) { total_solicitudes total_abiertas total_cerradas tasa_resolucion promedio_dias_total promedio_respuesta promedio_por_estado { estado promedio_dias cantidad } ranking_proyectos { proyecto cantidad abiertas cerradas } tendencia_mensual { mes creadas cerradas } solicitudes_vencidas { id proyecto_nombre apartamento estado dias_sin_movimiento prioridad } } }`;
      } else {
        q = `{ posventaInformeGlobal { total_solicitudes total_abiertas total_cerradas tasa_resolucion promedio_dias_total promedio_respuesta promedio_por_estado { estado promedio_dias cantidad } ranking_proyectos { proyecto cantidad abiertas cerradas } tendencia_mensual { mes creadas cerradas } solicitudes_vencidas { id proyecto_nombre apartamento estado dias_sin_movimiento prioridad } } }`;
      }
      const d = await client.query(q, vars);
      setInforme(d.posventaInformeGlobal);
      setVista('informes');
    } catch (err) { console.error(err); error('Error cargando informe'); }
  };

  const cargarConsolidado = async () => {
    try {
      const d = await client.query(`{ posventaConsolidado { total_general total_facturas total_apartamentos proyectos { proyecto_id proyecto_nombre apartamento total cantidad solicitudes abiertas vinculado } } }`);
      setConsolidado(d.posventaConsolidado);
      setVista('consolidado');
    } catch (err) { console.error(err); error('Error cargando el consolidado'); }
  };

  const verDetalle = async (sol) => {
    try {
      const d = await client.query(`query($id: Int!) { posventaInformeIndividual(solicitud_id: $id) { dias_total tiempos { estado fecha_inicio fecha_fin dias } solicitud { id proyecto_nombre apartamento estado prioridad } } }`, { id: sol.id });
      setDetalle(d.posventaInformeIndividual);
    } catch { error('Error cargando detalle'); }
  };

  // Visitas que el ingeniero registro desde la app movil. Modal propio: no tiene
  // nada que ver con los tiempos de estado del tablero.
  const verVisitas = async (sol) => {
    setVisitasSol(sol);
    setVisitasLoading(true);
    setVisitas([]);
    try {
      const v = await client.query(`query($id: Int!) { posventaVisitas(solicitud_id: $id) { id fecha_visita resumen usuario_nombre origen created_at evidencias { id ruta_archivo nombre_archivo } } }`, { id: sol.id });
      setVisitas(v.posventaVisitas || []);
    } catch {
      error('Error cargando las visitas');
    } finally {
      setVisitasLoading(false);
    }
  };

  const cerrarVisitas = () => { setVisitasSol(null); setVisitas([]); };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Cargando...</div>;

  const porEstado = (estado) => solicitudes
    .filter(s => s.estado === estado)
    .filter(s => !soloSinLeer || esNueva(s))
    .filter(s => filtroOrigen === 'todos' || (filtroOrigen === 'bot' ? s.origen === 'bot' : s.origen !== 'bot'))
    .sort((a, b) => (esNueva(b) - esNueva(a)) || ((PRIORIDAD_ORDEN[a.prioridad] || 1) - (PRIORIDAD_ORDEN[b.prioridad] || 1)));
  const totalSinLeer = solicitudes.filter(esNueva).length;

  return (
    <div className="pv-container">
      <div className="pv-header">
        <div className="pv-header-left">
          <h2>Posventa - Garantias</h2>
          <p>Gestión de solicitudes de garantía por proyecto</p>
        </div>
        <div className="pv-header-actions">
          <button className={`pv-btn pv-btn-secondary ${vista === 'kanban' ? 'active' : ''}`} onClick={() => setVista('kanban')}>Tablero</button>
          <button className={`pv-btn pv-btn-secondary ${vista === 'consolidado' ? 'active' : ''}`} onClick={cargarConsolidado}>Consolidado</button>
          <button className={`pv-btn pv-btn-secondary ${vista === 'informes' ? 'active' : ''}`} onClick={cargarInforme}>Informes</button>
          <button className="pv-btn pv-btn-primary" onClick={() => { setEditando(null); setForm({ ...FORM_VACIO }); setBusquedaProy(''); setShowForm(true); }}>
            + Nueva Solicitud
          </button>
        </div>
      </div>

      {/* ===== FILTROS DEL TABLERO ===== */}
      {vista === 'kanban' && (
        <div className="pv-filtros">
          <button className={`pv-chip ${soloSinLeer ? 'activo' : ''}`} onClick={() => setSoloSinLeer(v => !v)}>
            Sin leer {totalSinLeer > 0 && <span className="pv-chip-num">{totalSinLeer}</span>}
          </button>
          <div className="pv-chip-group">
            {[['todos', 'Todas'], ['bot', 'WhatsApp'], ['sistema', 'Sistema']].map(([k, l]) => (
              <button key={k} className={`pv-chip ${filtroOrigen === k ? 'activo' : ''}`} onClick={() => setFiltroOrigen(k)}>{l}</button>
            ))}
          </div>
        </div>
      )}

      {/* ===== KANBAN ===== */}
      {vista === 'kanban' && (
        <div className="pv-kanban">
          {ESTADOS.map(({ key, label }) => (
            <div key={key} className={`pv-column pv-col-${key}`}
              onDragOver={(e) => handleDragOver(e, key)} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, key)}>
              <div className="pv-column-header">
                <span className="pv-column-title">{label}</span>
                <span className="pv-column-count">{porEstado(key).length}</span>
              </div>
              <div className={`pv-column-body ${dragOver === key ? 'drag-over' : ''}`}>
                {porEstado(key).map(sol => (
                  <div key={sol.id} className={`pv-card ${dragItem?.id === sol.id ? 'dragging' : ''} ${esNueva(sol) ? 'pv-card-nueva' : ''}`}
                    draggable onDragStart={(e) => handleDragStart(e, sol)}
                    style={{ borderLeft: `4px solid ${getPrioridadColor(sol.prioridad)}` }}>
                    <div className="pv-card-top">
                      <div className="pv-card-title">{sol.proyecto_nombre}</div>
                      <span className={`pv-prioridad-badge pv-prio-${sol.prioridad}`}>
                        {PRIORIDADES.find(p => p.key === sol.prioridad)?.label || 'Media'}
                      </span>
                    </div>
                    {(sol.origen === 'bot' || esNueva(sol)) && (
                      <div className="pv-card-badges">
                        {sol.origen === 'bot' && <span className="pv-badge pv-badge-wa">WhatsApp</span>}
                        {esNueva(sol) && <span className="pv-badge pv-badge-nuevo">Nuevo</span>}
                      </div>
                    )}
                    {sol.apartamento && <div className="pv-card-apto">Apto {sol.apartamento}</div>}
                    {sol.solicitante_nombre && (
                      <div className="pv-card-solicitante">&#128100; {sol.solicitante_nombre}{sol.solicitante_tipo ? ` · ${tipoSolicitanteLabel(sol.solicitante_tipo)}` : ''}</div>
                    )}
                    {sol.descripcion && <div className="pv-card-desc">{sol.descripcion}</div>}
                    <div className="pv-card-footer">
                      <span className="pv-card-date">
                        {formatDate(sol.created_at)}
                        {(sol.adjuntos || []).length > 0 && <span className="pv-card-adj" title="Fotos y videos adjuntos"> · &#128206; {sol.adjuntos.length}</span>}
                      </span>
                      <div className="pv-card-actions">
                        <button className="pv-card-btn" onClick={() => verFicha(sol)} title="Ver ficha y adjuntos">&#128203;</button>
                        <button className="pv-card-btn" onClick={() => verGasto(sol)} title="Ver gasto (facturas)">&#128181;</button>
                        <button className="pv-card-btn" onClick={() => verDetalle(sol)} title="Ver tiempos">&#128340;</button>
                        <button className="pv-card-btn" onClick={() => verVisitas(sol)} title="Visitas del ingeniero">&#128247;</button>
                        <button className="pv-card-btn" onClick={() => handleEditar(sol)} title="Editar">&#9998;</button>
                        <button className="pv-card-btn danger" onClick={() => handleEliminar(sol.id)} title="Eliminar">&#10005;</button>
                      </div>
                    </div>
                  </div>
                ))}
                {porEstado(key).length === 0 && <div style={{ textAlign: 'center', color: '#cbd5e1', fontSize: '0.78rem', padding: 20 }}>Sin solicitudes</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===== INFORMES ===== */}
      {vista === 'informes' && informe && (
        <div className="pv-informes">
          {/* Filtro de fechas */}
          <div className="pv-filtro-fechas">
            <div className="pv-filtro-group">
              <label>Desde</label>
              <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
            </div>
            <div className="pv-filtro-group">
              <label>Hasta</label>
              <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
            </div>
            <button className="pv-btn pv-btn-primary" onClick={cargarInforme} style={{ alignSelf: 'flex-end' }}>Aplicar</button>
            {(fechaDesde || fechaHasta) && (
              <button className="pv-btn pv-btn-secondary" onClick={() => { setFechaDesde(''); setFechaHasta(''); }} style={{ alignSelf: 'flex-end' }}>Limpiar</button>
            )}
          </div>

          {/* KPIs principales */}
          <div className="pv-kpi-grid">
            <div className="pv-kpi-card">
              <div className="pv-kpi-value">{informe.total_solicitudes}</div>
              <div className="pv-kpi-label">Total Solicitudes</div>
            </div>
            <div className="pv-kpi-card">
              <div className="pv-kpi-value" style={{ color: '#f59e0b' }}>{informe.total_abiertas}</div>
              <div className="pv-kpi-label">Abiertas</div>
            </div>
            <div className="pv-kpi-card">
              <div className="pv-kpi-value" style={{ color: '#22c55e' }}>{informe.total_cerradas}</div>
              <div className="pv-kpi-label">Cerradas</div>
            </div>
            <div className="pv-kpi-card">
              <div className="pv-kpi-value" style={{ color: '#6366f1' }}>{informe.tasa_resolucion}%</div>
              <div className="pv-kpi-label">Tasa de Resolución</div>
            </div>
          </div>

          {/* KPIs de tiempos */}
          <div className="pv-kpi-grid pv-kpi-grid-3">
            <div className="pv-kpi-card pv-kpi-accent">
              <div className="pv-kpi-value">{informe.promedio_respuesta}d</div>
              <div className="pv-kpi-label">Prom. Tiempo Respuesta</div>
              <div className="pv-kpi-sub">Radicación → Visita</div>
            </div>
            <div className="pv-kpi-card pv-kpi-accent">
              <div className="pv-kpi-value">{informe.promedio_dias_total}d</div>
              <div className="pv-kpi-label">Prom. Resolución Total</div>
              <div className="pv-kpi-sub">Solo garantías cerradas</div>
            </div>
            <div className="pv-kpi-card pv-kpi-accent">
              <div className="pv-kpi-value">{informe.solicitudes_vencidas?.length || 0}</div>
              <div className="pv-kpi-label">Solicitudes Vencidas</div>
              <div className="pv-kpi-sub">+15 días sin movimiento</div>
            </div>
          </div>

          {/* Gráfica de tendencia */}
          <div className="pv-chart-section">
            <h3>Tendencia Mensual</h3>
            <div className="pv-chart-container">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={informe.tendencia_mensual} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradCreadas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradCerradas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
                  <Area type="monotone" dataKey="creadas" stroke="#6366f1" strokeWidth={2.5} fill="url(#gradCreadas)" name="Creadas" dot={{ r: 4, fill: '#6366f1' }} />
                  <Area type="monotone" dataKey="cerradas" stroke="#22c55e" strokeWidth={2.5} fill="url(#gradCerradas)" name="Cerradas" dot={{ r: 4, fill: '#22c55e' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Promedio por estado */}
          <div className="pv-chart-section">
            <h3>Promedio de Días por Estado</h3>
            <div className="pv-estado-bars">
              {informe.promedio_por_estado.map((p, i) => {
                const maxDias = Math.max(...informe.promedio_por_estado.map(x => x.promedio_dias), 1);
                const pct = (p.promedio_dias / maxDias) * 100;
                const colors = ['#fbbf24', '#3b82f6', '#8b5cf6', '#22c55e'];
                return (
                  <div key={i} className="pv-estado-bar-row">
                    <div className="pv-estado-bar-label">{p.estado}</div>
                    <div className="pv-estado-bar-track">
                      <div className="pv-estado-bar-fill" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${colors[i]}88, ${colors[i]})` }} />
                    </div>
                    <div className="pv-estado-bar-value">{p.promedio_dias}d <span>({p.cantidad})</span></div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="pv-two-cols">
            {/* Ranking proyectos */}
            <div className="pv-chart-section">
              <h3>Proyectos con más Solicitudes</h3>
              <table className="pv-informe-table">
                <thead><tr><th>Proyecto</th><th>Total</th><th>Abiertas</th><th>Cerradas</th></tr></thead>
                <tbody>
                  {(informe.ranking_proyectos || []).slice(0, 10).map((r, i) => (
                    <tr key={i}><td><strong>{r.proyecto}</strong></td><td>{r.cantidad}</td><td style={{ color: '#f59e0b' }}>{r.abiertas}</td><td style={{ color: '#22c55e' }}>{r.cerradas}</td></tr>
                  ))}
                  {(!informe.ranking_proyectos || informe.ranking_proyectos.length === 0) && <tr><td colSpan="4" style={{ textAlign: 'center', color: '#94a3b8' }}>Sin datos</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Solicitudes vencidas */}
            <div className="pv-chart-section">
              <h3>Solicitudes Vencidas (+15 días)</h3>
              {(informe.solicitudes_vencidas || []).length > 0 ? (
                <div className="pv-vencidas-list">
                  {informe.solicitudes_vencidas.map(v => (
                    <div key={v.id} className="pv-vencida-item" style={{ borderLeft: `4px solid ${getPrioridadColor(v.prioridad)}` }}>
                      <div><strong>{v.proyecto_nombre}</strong> {v.apartamento ? `Apto ${v.apartamento}` : ''}</div>
                      <div className="pv-vencida-info">
                        <span>{v.estado}</span>
                        <span className="pv-vencida-dias">{v.dias_sin_movimiento} días</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: '#22c55e', padding: 30, fontSize: '0.88rem' }}>Sin solicitudes vencidas</div>
              )}
            </div>
          </div>

          {/* Detalle por solicitud */}
          <div className="pv-chart-section">
            <h3>Detalle por Solicitud</h3>
            <table className="pv-informe-table">
              <thead><tr><th>Proyecto</th><th>Apto</th><th>Prioridad</th><th>Estado</th><th>Creada</th><th></th></tr></thead>
              <tbody>
                {solicitudes.map(sol => (
                  <tr key={sol.id}>
                    <td><strong>{sol.proyecto_nombre}</strong></td>
                    <td>{sol.apartamento || '-'}</td>
                    <td><span className={`pv-prioridad-badge pv-prio-${sol.prioridad}`}>{PRIORIDADES.find(p => p.key === sol.prioridad)?.label || 'Media'}</span></td>
                    <td>{ESTADOS.find(e => e.key === sol.estado)?.label || sol.estado}</td>
                    <td>{formatDate(sol.created_at)}</td>
                    <td><button className="pv-btn pv-btn-secondary" style={{ padding: '4px 10px', fontSize: '0.76rem' }} onClick={() => verDetalle(sol)}>Ver</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== CONSOLIDADO (DASHBOARD) ===== */}
      {vista === 'consolidado' && consolidado && (
        <div className="pv-informes">
          <div className="pv-kpi-grid pv-kpi-grid-3">
            <div className="pv-kpi-card">
              <div className="pv-kpi-value" style={{ color: '#15803d' }}>{formatMoney(consolidado.total_general)}</div>
              <div className="pv-kpi-label">Total gastado en Posventa</div>
            </div>
            <div className="pv-kpi-card">
              <div className="pv-kpi-value" style={{ color: '#14b8a6' }}>{consolidado.total_apartamentos}</div>
              <div className="pv-kpi-label">Apartamentos con posventa</div>
            </div>
            <div className="pv-kpi-card">
              <div className="pv-kpi-value" style={{ color: '#6366f1' }}>{consolidado.total_facturas}</div>
              <div className="pv-kpi-label">Facturas de garantía</div>
            </div>
          </div>

          {consolidado.proyectos.some(p => p.total > 0) && (
            <div className="pv-chart-section">
              <h3>Top apartamentos por gasto</h3>
              <div className="pv-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={consolidado.proyectos.filter(p => p.total > 0).slice(0, 10).map(p => ({
                      nombre: `${p.proyecto_nombre || 'Sin vincular'}${p.apartamento ? ' ' + p.apartamento : ''}`.trim(),
                      total: p.total,
                    }))}
                    margin={{ top: 10, right: 20, left: 10, bottom: 70 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="nombre" tick={{ fontSize: 10, fill: '#94a3b8' }} angle={-35} textAnchor="end" interval={0} height={80} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => formatMoney(v)} contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
                    <Bar dataKey="total" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="pv-chart-section">
            <h3>Detalle por apartamento</h3>
            <table className="pv-informe-table">
              <thead>
                <tr>
                  <th>Proyecto</th><th>Apto</th>
                  <th style={{ textAlign: 'center' }}>Garantías</th>
                  <th style={{ textAlign: 'center' }}>Abiertas</th>
                  <th style={{ textAlign: 'center' }}>Facturas</th>
                  <th style={{ textAlign: 'right' }}>Total gastado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {consolidado.proyectos.map((p, i) => (
                  <tr key={i}>
                    <td>
                      <strong>{p.proyecto_nombre || 'Sin vincular'}</strong>
                      {!p.vinculado && <span style={{ marginLeft: 6, background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: 6, fontSize: '0.7rem' }}>sin vincular</span>}
                    </td>
                    <td>{p.apartamento || '-'}</td>
                    <td style={{ textAlign: 'center' }}>{p.solicitudes}</td>
                    <td style={{ textAlign: 'center', color: p.abiertas > 0 ? '#f59e0b' : '#94a3b8' }}>{p.abiertas}</td>
                    <td style={{ textAlign: 'center' }}>{p.cantidad}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{formatMoney(p.total)}</td>
                    <td><button className="pv-btn pv-btn-secondary" style={{ padding: '3px 9px' }} onClick={() => verGasto(p)} title="Ver facturas">&#128181;</button></td>
                  </tr>
                ))}
                {consolidado.proyectos.length === 0 && (
                  <tr><td colSpan="7" style={{ textAlign: 'center', color: '#94a3b8', padding: 20 }}>Sin posventa registrada</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== MODAL CREAR/EDITAR ===== */}
      {showForm && (
        <div className="pv-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="pv-modal" onClick={e => e.stopPropagation()}>
            <h3>{editando ? 'Editar Solicitud' : 'Nueva Solicitud de Garantia'}</h3>
            <div className="pv-form-group">
              <label>Proyecto <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.8em' }}>(opcional — búscalo para autocompletar)</span></label>
              <input type="text" value={busquedaProy} onChange={e => buscarProyectos(e.target.value)} placeholder="Buscar proyecto por nombre o apartamento..." />
              {showProyResults && proyectos.length > 0 && (
                <div className="pv-search-results">
                  {proyectos.map(p => (
                    <div key={p.id} className="pv-search-item" onClick={() => seleccionarProyecto(p)}>
                      <strong>{p.proyecto_nombre}</strong> {p.apartamento ? `- Apto ${p.apartamento}` : ''} <span style={{ color: '#94a3b8' }}>({p.nombre_cliente})</span>
                    </div>
                  ))}
                </div>
              )}
              {form.proyecto_id && <div style={{ fontSize: '0.76rem', color: '#2563eb', marginTop: 4 }}>✓ Vinculado a un proyecto de la lista</div>}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div className="pv-form-group" style={{ flex: 2 }}>
                <label>Nombre del proyecto *</label>
                <input type="text" value={form.proyecto_nombre}
                  onChange={e => setForm({ ...form, proyecto_nombre: e.target.value, proyecto_id: null })}
                  placeholder="Nombre del proyecto" />
              </div>
              <div className="pv-form-group" style={{ flex: 1 }}>
                <label># Apto</label>
                <input type="text" value={form.apartamento}
                  onChange={e => setForm({ ...form, apartamento: e.target.value, proyecto_id: null })}
                  placeholder="Ej: 401" />
              </div>
            </div>
            <div className="pv-form-group">
              <label>Prioridad</label>
              <select value={form.prioridad} onChange={e => setForm({ ...form, prioridad: e.target.value })}>
                {PRIORIDADES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div className="pv-form-row-2">
              <div className="pv-form-group">
                <label>Quién reporta <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.8em' }}>(opcional)</span></label>
                <input type="text" value={form.solicitante_nombre} onChange={e => setForm({ ...form, solicitante_nombre: e.target.value })} placeholder="Nombre" />
              </div>
              <div className="pv-form-group">
                <label>Teléfono</label>
                <input type="text" value={form.solicitante_telefono} onChange={e => setForm({ ...form, solicitante_telefono: e.target.value })} placeholder="Ej: 310 123 4567" />
              </div>
            </div>
            <div className="pv-form-row-2">
              <div className="pv-form-group">
                <label>Es</label>
                <select value={form.solicitante_tipo} onChange={e => setForm({ ...form, solicitante_tipo: e.target.value })}>
                  <option value="">—</option>
                  {TIPOS_SOLICITANTE.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <div className="pv-form-group">
                <label>Fecha del daño</label>
                <input type="date" value={form.fecha_dano} onChange={e => setForm({ ...form, fecha_dano: e.target.value })} />
              </div>
            </div>
            <div className="pv-form-group">
              <label>Descripcion</label>
              <textarea value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} placeholder="Describe el problema o solicitud de garantia..." />
            </div>
            <div className="pv-form-actions">
              <button className="pv-btn pv-btn-secondary" onClick={() => { setShowForm(false); setEditando(null); }}>Cancelar</button>
              <button className="pv-btn pv-btn-primary" onClick={handleGuardar} disabled={guardando}>{guardando ? 'Guardando...' : editando ? 'Guardar' : 'Crear Solicitud'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL FICHA DE LA SOLICITUD (solicitante + adjuntos) ===== */}
      {ficha && (
        <div className="pv-modal-overlay" onClick={() => setFicha(null)}>
          <div className="pv-modal pv-detalle-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <h3 style={{ margin: 0 }}>Solicitud #{ficha.id} · {ficha.proyecto_nombre} {ficha.apartamento ? `Apto ${ficha.apartamento}` : ''}</h3>
              {ficha.origen === 'bot' && <span className="pv-badge pv-badge-wa">WhatsApp</span>}
              <span className={`pv-prioridad-badge pv-prio-${ficha.prioridad}`}>{PRIORIDADES.find(p => p.key === ficha.prioridad)?.label || 'Media'}</span>
            </div>
            <div style={{ color: '#64748b', fontSize: 13, marginBottom: 14 }}>
              {ESTADOS.find(e => e.key === ficha.estado)?.label || ficha.estado}
              {ficha.estado_residente && <span> · el residente lo ve como <strong>{ficha.estado_residente}</strong></span>}
              {ficha.leida_por && <span> · leída por {ficha.leida_por}</span>}
            </div>
            <table className="pv-ficha-tabla">
              <tbody>
                <tr><td>Quién reporta</td><td><strong>{ficha.solicitante_nombre || '—'}</strong>{ficha.solicitante_tipo ? ` · ${tipoSolicitanteLabel(ficha.solicitante_tipo)}` : ''}</td></tr>
                <tr><td>Teléfono</td><td>{ficha.solicitante_telefono ? <a href={`https://wa.me/${ficha.solicitante_telefono}`} target="_blank" rel="noreferrer">{ficha.solicitante_telefono}</a> : '—'}{ficha.whatsapp && ficha.whatsapp !== ficha.solicitante_telefono ? <span style={{ color: '#64748b' }}> · WhatsApp {ficha.whatsapp}</span> : null}</td></tr>
                <tr><td>Fecha del daño</td><td>{ficha.fecha_dano ? formatDate(ficha.fecha_dano + 'T12:00:00') : '—'}</td></tr>
                <tr><td>Radicada</td><td>{formatDateTime(ficha.created_at)}{ficha.proyecto_id ? <span style={{ color: '#2563eb' }}> · vinculada a un proyecto</span> : <span style={{ color: '#b45309' }}> · sin vincular a proyecto</span>}</td></tr>
              </tbody>
            </table>
            <p style={{ margin: '12px 0 4px', fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Descripción del daño</p>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 13.5, color: '#334155', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{ficha.descripcion || 'Sin descripción'}</div>
            <p style={{ margin: '14px 0 6px', fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Fotos y videos ({(ficha.adjuntos || []).length})</p>
            {(ficha.adjuntos || []).length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic', margin: 0 }}>Sin archivos adjuntos.</p>
            ) : (
              <div className="pv-galeria">
                {ficha.adjuntos.map(a => (
                  a.tipo === 'foto' ? (
                    <a key={a.id} className="pv-galeria-item" href={urlAdjunto(a.ruta_archivo)} target="_blank" rel="noreferrer" title={a.nombre_archivo || 'Foto'}>
                      <img src={urlAdjunto(a.ruta_archivo)} alt={a.nombre_archivo || 'Foto'} />
                    </a>
                  ) : a.tipo === 'video' ? (
                    <div key={a.id} className="pv-galeria-item pv-galeria-video" title={a.nombre_archivo || 'Video'}>
                      <video src={urlAdjunto(a.ruta_archivo)} controls preload="metadata" />
                      <a href={urlAdjunto(a.ruta_archivo)} target="_blank" rel="noreferrer">Abrir video{a.tamano_bytes ? ` · ${formatBytes(a.tamano_bytes)}` : ''}</a>
                    </div>
                  ) : (
                    <a key={a.id} className="pv-galeria-item pv-galeria-doc" href={urlAdjunto(a.ruta_archivo)} target="_blank" rel="noreferrer" title={a.nombre_archivo || 'Archivo'}>
                      <span>&#128196;</span><small>{a.nombre_archivo || a.tipo}{a.tamano_bytes ? ` · ${formatBytes(a.tamano_bytes)}` : ''}</small>
                    </a>
                  )
                ))}
              </div>
            )}
            <div className="pv-form-actions">
              <button className="pv-btn pv-btn-secondary" onClick={() => { const s = ficha; setFicha(null); handleEditar(s); }}>Editar</button>
              <button className="pv-btn pv-btn-secondary" onClick={() => setFicha(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL DETALLE INDIVIDUAL ===== */}
      {detalle && (
        <div className="pv-modal-overlay" onClick={() => setDetalle(null)}>
          <div className="pv-modal pv-detalle-modal" onClick={e => e.stopPropagation()}>
            <h3>Tiempos - {detalle.solicitud.proyecto_nombre} {detalle.solicitud.apartamento ? `Apto ${detalle.solicitud.apartamento}` : ''}</h3>
            <div className="pv-detalle-total">
              <strong>{detalle.dias_total} dias</strong>
              <span>Tiempo total</span>
            </div>
            <ul className="pv-timeline">
              {detalle.tiempos.map((t, i) => (
                <li key={i} className="pv-timeline-item">
                  <div className="pv-timeline-dot" />
                  <div>
                    <div className="pv-timeline-estado">{t.estado}</div>
                    <div className="pv-timeline-fecha">{formatDateTime(t.fecha_inicio)} → {formatDateTime(t.fecha_fin)}</div>
                    <div className="pv-timeline-dias">{t.dias} dias</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="pv-form-actions">
              <button className="pv-btn pv-btn-secondary" onClick={() => setDetalle(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL VISITAS DEL INGENIERO ===== */}
      {visitasSol && (
        <div className="pv-modal-overlay" onClick={cerrarVisitas}>
          <div className="pv-modal pv-detalle-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <h3>
              Visitas - {visitasSol.proyecto_nombre} {visitasSol.apartamento ? `Apto ${visitasSol.apartamento}` : ''}
            </h3>
            <p style={{ margin: '-6px 0 16px', color: '#64748b', fontSize: 13 }}>
              {visitasSol.descripcion}
            </p>

            {visitasLoading ? (
              <p style={{ color: '#64748b', fontSize: 14 }}>Cargando visitas...</p>
            ) : visitas.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: 14, fontStyle: 'italic' }}>
                Aun no hay visitas registradas desde la app para esta garantia.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {visitas.map(v => (
                  <div key={v.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <strong style={{ color: '#1e3a5f', fontSize: 14 }}>{formatDate(v.fecha_visita)}</strong>
                      <span style={{ fontSize: 12, color: '#64748b' }}>{v.usuario_nombre || 'Sin autor'}</span>
                    </div>
                    {v.resumen && (
                      <p style={{ margin: '8px 0 0', fontSize: 13, color: '#334155', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                        {v.resumen}
                      </p>
                    )}
                    {(v.evidencias || []).length > 0 && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                        {v.evidencias.map(e => (
                          <a
                            key={e.id}
                            href={`${UPLOAD_URL}/catalogo/img/evidencia-posventa/${e.ruta_archivo}`}
                            target="_blank"
                            rel="noreferrer"
                            title={e.nombre_archivo || 'Evidencia'}
                          >
                            <img
                              src={`${UPLOAD_URL}/catalogo/img/evidencia-posventa/${e.ruta_archivo}`}
                              alt={e.nombre_archivo || 'Evidencia'}
                              style={{ width: 104, height: 104, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }}
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="pv-form-actions">
              <button className="pv-btn pv-btn-secondary" onClick={cerrarVisitas}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL GASTO POR APARTAMENTO ===== */}
      {gastoDetalle && (
        <div className="pv-modal-overlay" onClick={() => setGastoDetalle(null)}>
          <div className="pv-modal pv-detalle-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 780 }}>
            <h3 style={{ marginBottom: 4 }}>&#128181; Gasto en Posventa</h3>
            <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: 14 }}>
              <strong>{gastoDetalle.proyecto_nombre}</strong>{gastoDetalle.apartamento ? ` · Apto ${gastoDetalle.apartamento}` : ''}
            </div>
            {!gastoDetalle.vinculado && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 8, padding: '9px 12px', marginBottom: 14, fontSize: '0.82rem' }}>
                ⚠️ Esta solicitud aún no está vinculada a un proyecto. Se muestran las facturas encontradas por apartamento. Para un control preciso, edítala y selecciona el proyecto.
              </div>
            )}
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 18px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#166534', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.03em' }}>TOTAL GASTADO EN GARANTÍAS</span>
              <span style={{ color: '#15803d', fontSize: '1.5rem', fontWeight: 800 }}>{formatMoney(gastoDetalle.total)}</span>
            </div>
            {gastoLoading ? (
              <div style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>Cargando facturas...</div>
            ) : (gastoDetalle.facturas || []).length === 0 ? (
              <div style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>Aún no hay facturas de posventa para este apartamento.</div>
            ) : (
              <table className="pv-informe-table">
                <thead><tr><th>Fecha</th><th>Proveedor</th><th>Descripción</th><th style={{ textAlign: 'right' }}>Monto</th><th style={{ textAlign: 'center' }}>Comp.</th></tr></thead>
                <tbody>
                  {gastoDetalle.facturas.map(f => (
                    <tr key={`${f.tipo || 'factura'}-${f.id}`}>
                      <td>{formatDate(f.fecha_emision)}</td>
                      <td>{f.proveedor_nombre || '-'}</td>
                      <td>
                        {f.descripcion || `Factura ${f.numero_factura}`}
                        {f.tipo === 'caja_menor' && (
                          <span style={{ marginLeft: 8, background: '#ede9fe', color: '#6d28d9', fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Caja Menor</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(f.monto)}</td>
                      <td style={{ textAlign: 'center' }}>
                        {(f.archivos && f.archivos.length > 0) ? (
                          <span style={{ display: 'inline-flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                            {f.archivos.map((a, i) => (
                              <button key={i} className="pv-btn pv-btn-secondary" style={{ padding: '3px 9px', fontSize: '0.9rem' }}
                                onClick={() => window.open(`${UPLOAD_URL}/${a.url}`, '_blank')}
                                title={a.nombre || 'Ver comprobante'}>&#128206;</button>
                            ))}
                          </span>
                        ) : f.archivo_factura ? (
                          <button className="pv-btn pv-btn-secondary" style={{ padding: '3px 9px', fontSize: '0.9rem' }}
                            onClick={() => window.open(`${UPLOAD_URL}/factura-file/${encodeURIComponent(f.archivo_factura)}`, '_blank')}
                            title="Ver comprobante">&#128206;</button>
                        ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="pv-form-actions" style={{ marginTop: 16 }}>
              <button className="pv-btn pv-btn-secondary" onClick={() => setGastoDetalle(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
